import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Plus, Link2, Pause, Play, Lock, Download, Eye, Loader2, ClipboardList, Mail, ArrowLeftRight, Trash2, QrCode,
} from "lucide-react";
import { InviteDialog } from "../../components/discovery/InviteDialog";
import { QrDialog } from "../../components/discovery/QrDialog";

const STATUS_BADGE = {
  open: { label: "Aperto", cls: "bg-success-subtle text-success-strong" },
  paused: { label: "In pausa", cls: "bg-warning-subtle text-warning-strong" },
  closed: { label: "Chiuso", cls: "bg-subtle text-text-muted" },
};

const fmtDT = (s) => (s ? new Date(s).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function SuperAdminDiscovery() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [inviting, setInviting] = useState(null);
  const [qrFor, setQrFor] = useState(null);

  const [failed, setFailed] = useState(false);
  const load = () => {
    setLoading(true);
    api.get("/discovery/assessments")
      .then(({ data }) => { setItems(data); setFailed(false); })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const copyLink = (a) => {
    const url = `${window.location.origin}/d/${a.slug_token}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success("Link copiato negli appunti"))
      .catch(() => toast.info(url));
  };

  const setStatus = async (a, status) => {
    try {
      await api.patch(`/discovery/assessments/${a.id}`, { status });
      toast.success(status === "open" ? "Raccolta riaperta" : status === "paused" ? "Raccolta in pausa" : "Raccolta chiusa");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore aggiornamento");
    }
  };

  const exportCsv = async (a, format) => {
    try {
      const { data } = await api.get(`/discovery/assessments/${a.id}/export`, {
        params: { format },
        responseType: format === "csv" ? "blob" : "json",
      });
      const blob = format === "csv" ? data : new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const el = document.createElement("a");
      el.href = url;
      el.download = `discovery-${a.title.slice(0, 30).replace(/\s+/g, "_")}.${format}`;
      el.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export non riuscito");
    }
  };

  const deleteAssessment = async (a) => {
    try {
      await api.delete(`/discovery/assessments/${a.id}`);
      toast.success("Assessment eliminato definitivamente");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Eliminazione non riuscita");
    }
  };

  return (
    <div className="space-y-6" data-testid="sa-discovery-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2.5">
            <ClipboardList size={22} className="text-accent" /> Discovery
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Assessment AI: crea un link, condividilo, raccogli risposte strutturate e contatti.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCompareOpen(true)} data-testid="discovery-compare-btn"
                  className="rounded-md" disabled={items.length < 2}>
            <ArrowLeftRight size={15} className="mr-1.5" /> Confronta
          </Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="discovery-create-btn"
                  className="bg-accent hover:bg-accent-strong text-white rounded-md">
            <Plus size={15} className="mr-1.5" /> Nuovo assessment
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-text-muted"><Loader2 className="animate-spin mx-auto" size={20} /></div>
      ) : failed ? (
        <div className="bg-surface border border-border rounded-xl">
          <LoadErrorBox what="gli assessment" onRetry={load} testId="discovery-load-error" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-surface border border-border rounded-xl p-12 text-center text-sm text-text-muted" data-testid="discovery-empty">
          Nessun assessment ancora. Creane uno e condividi il link.
        </div>
      ) : (
        <div className="bg-surface border border-border rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-subtle text-xs uppercase tracking-wider text-text-secondary">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Titolo</th>
                <th className="text-left px-3 py-3 font-medium">Stato</th>
                <th className="text-right px-3 py-3 font-medium">Risposte</th>
                <th className="text-right px-3 py-3 font-medium">Consensi</th>
                <th className="text-left px-3 py-3 font-medium">Ultima risposta</th>
                <th className="text-right px-5 py-3 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => {
                const b = STATUS_BADGE[a.status] || STATUS_BADGE.open;
                return (
                  <tr key={a.id} className="border-t border-border" data-testid={`discovery-row-${a.id}`}>
                    <td className="px-5 py-3 cursor-pointer" onClick={() => navigate(`/superadmin/discovery/${a.id}`)}>
                      <div className="text-text-primary font-medium hover:text-accent-strong">{a.title}</div>
                      <div className="text-[11px] text-text-muted">{a.sector || "—"} · contatto: {a.settings?.contact} · reazioni: {a.settings?.reactions ? "on" : "off"}</div>
                    </td>
                    <td className="px-3 py-3"><span className={`text-xs px-2 py-0.5 rounded-md ${b.cls}`}>{b.label}</span></td>
                    <td className="px-3 py-3 text-right text-text-primary" data-testid={`discovery-nresp-${a.id}`}>{a.n_responses}</td>
                    <td className="px-3 py-3 text-right text-text-primary" data-testid={`discovery-nconsent-${a.id}`}>
                      {a.n_contacts} <span className="text-text-muted text-xs">su {a.n_responses}</span>
                    </td>
                    <td className="px-3 py-3 text-text-secondary text-xs">{fmtDT(a.last_response_at)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <IconBtn title="Copia link" onClick={() => copyLink(a)} testId={`discovery-copy-${a.id}`}><Link2 size={14} /></IconBtn>
                        <IconBtn title="QR code" onClick={() => setQrFor(a)} testId={`discovery-qr-${a.id}`}><QrCode size={14} /></IconBtn>
                        <IconBtn title="Invita via email" onClick={() => setInviting(a)} testId={`discovery-invite-${a.id}`}><Mail size={14} /></IconBtn>
                        <IconBtn title="Apri dashboard" onClick={() => navigate(`/superadmin/discovery/${a.id}`)} testId={`discovery-view-${a.id}`}><Eye size={14} /></IconBtn>
                        <IconBtn title="Esporta CSV" onClick={() => exportCsv(a, "csv")} testId={`discovery-export-${a.id}`}><Download size={14} /></IconBtn>
                        {a.status === "open" && (
                          <IconBtn title="Metti in pausa" onClick={() => setStatus(a, "paused")} testId={`discovery-pause-${a.id}`}><Pause size={14} /></IconBtn>
                        )}
                        {a.status === "paused" && (
                          <IconBtn title="Riapri" onClick={() => setStatus(a, "open")} testId={`discovery-reopen-${a.id}`}><Play size={14} /></IconBtn>
                        )}
                        {a.status !== "closed" && (
                          <IconBtn title="Chiudi raccolta" danger testId={`discovery-close-${a.id}`}
                                   onClick={() => window.confirm(`Chiudere la raccolta "${a.title}"? Il link smetterà di accettare risposte.`) && setStatus(a, "closed")}>
                            <Lock size={14} />
                          </IconBtn>
                        )}
                        <IconBtn title="Elimina definitivamente" danger testId={`discovery-delete-${a.id}`}
                                 onClick={() => window.confirm(`Eliminare DEFINITIVAMENTE "${a.title}"?\nVerranno cancellate anche le ${a.n_responses} risposte, i contatti e gli inviti. Azione irreversibile.`) && deleteAssessment(a)}>
                          <Trash2 size={14} />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />
      <InviteDialog assessment={inviting} onClose={() => setInviting(null)} />
      {qrFor && <QrDialog assessment={qrFor} onClose={() => setQrFor(null)} />}
      <CompareDialog open={compareOpen} items={items} onClose={() => setCompareOpen(false)} />
    </div>
  );
}

const IconBtn = ({ children, title, onClick, danger, testId }) => (
  <button type="button" title={title} onClick={onClick} data-testid={testId}
          className={`h-8 w-8 inline-flex items-center justify-center rounded-md ${danger ? "text-danger hover:bg-danger-subtle" : "text-text-secondary hover:bg-subtle hover:text-text-primary"}`}>
    {children}
  </button>
);

const CreateDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({ title: "", sector: "", internal_notes: "", contact: "required", reactions: true });
  const [busy, setBusy] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/discovery/assessments", {
        title: form.title.trim(),
        sector: form.sector.trim() || null,
        internal_notes: form.internal_notes.trim() || null,
        settings: { contact: form.contact, reactions: form.reactions },
      });
      setCreatedLink(`${window.location.origin}/d/${data.slug_token}`);
      toast.success("Assessment creato");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore creazione");
    } finally {
      setBusy(false);
    }
  };

  const closeAll = () => {
    setForm({ title: "", sector: "", internal_notes: "", contact: "required", reactions: true });
    if (createdLink) onCreated(); else onClose();
    setCreatedLink(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeAll()}>
      <DialogContent className="rounded-xl bg-surface sm:max-w-md" data-testid="discovery-create-dialog">
        <DialogHeader>
          <DialogTitle>Nuovo assessment</DialogTitle>
          <DialogDescription>Un link unico da condividere: email, sponsorizzata, bio.</DialogDescription>
        </DialogHeader>
        {createdLink ? (
          <div className="space-y-4" data-testid="discovery-created-link-box">
            <p className="text-sm text-text-secondary">Link pubblico pronto:</p>
            <div className="bg-subtle border border-border rounded-lg px-3 py-2.5 font-mono text-xs break-all" data-testid="discovery-created-link">
              {createdLink}
            </div>
            <Button onClick={() => { navigator.clipboard.writeText(createdLink); toast.success("Link copiato"); }}
                    data-testid="discovery-created-copy" className="bg-accent hover:bg-accent-strong text-white w-full rounded-md">
              <Link2 size={14} className="mr-1.5" /> Copia link
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-text-secondary">Titolo (azienda prospect o campagna) *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={120}
                     placeholder="es. ACME Energia — discovery marzo" className="mt-1.5 h-10 rounded-md border-border bg-surface"
                     data-testid="discovery-title-input" />
            </div>
            <div>
              <Label className="text-xs font-medium text-text-secondary">Settore</Label>
              <Input value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} maxLength={80}
                     placeholder="es. energia, PMI manifatturiera" className="mt-1.5 h-10 rounded-md border-border bg-surface"
                     data-testid="discovery-sector-input" />
            </div>
            <div>
              <Label className="text-xs font-medium text-text-secondary">Note interne</Label>
              <Input value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} maxLength={2000}
                     placeholder="Solo per te" className="mt-1.5 h-10 rounded-md border-border bg-surface"
                     data-testid="discovery-notes-input" />
            </div>
            <div>
              <Label className="text-xs font-medium text-text-secondary">Blocco contatto finale</Label>
              <Select value={form.contact} onValueChange={(v) => setForm({ ...form, contact: v })}>
                <SelectTrigger className="mt-1.5 h-10 rounded-md border-border bg-surface" data-testid="discovery-contact-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="required">Obbligatorio (es. sponsorizzata)</SelectItem>
                  <SelectItem value="optional">Facoltativo (es. dipendenti prospect)</SelectItem>
                  <SelectItem value="off">Disattivato (solo mini-risultato)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border bg-subtle px-4 py-3">
              <div className="pr-3">
                <div className="text-sm font-medium text-text-primary">Micro-reazioni "voce di Mara"</div>
                <div className="text-[11px] text-text-muted">Spegnile per un tono più istituzionale.</div>
              </div>
              <Switch checked={form.reactions} onCheckedChange={(v) => setForm({ ...form, reactions: v })}
                      data-testid="discovery-reactions-switch" />
            </div>
          </div>
        )}
        {!createdLink && (
          <DialogFooter>
            <Button variant="outline" onClick={closeAll}>Annulla</Button>
            <Button onClick={create} disabled={busy || form.title.trim().length < 2}
                    data-testid="discovery-create-submit" className="bg-accent hover:bg-accent-strong text-white">
              {busy ? "Creazione…" : "Crea e genera link"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CompareDialog = ({ open, items, onClose }) => {
  const navigate = useNavigate();
  const [idA, setIdA] = useState("");
  const [idB, setIdB] = useState("");

  useEffect(() => {
    if (open) { setIdA(""); setIdB(""); }
  }, [open]);

  const opts = items.map((x) => ({ id: x.id, label: `${x.title} (${x.n_responses} risposte)` }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-xl bg-surface sm:max-w-md" data-testid="discovery-compare-dialog">
        <DialogHeader>
          <DialogTitle>Confronta due assessment</DialogTitle>
          <DialogDescription>
            Fianco a fianco: indici e risposte per capire quale prospect ha più margine di automazione.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-medium text-text-secondary">Assessment A</Label>
            <Select value={idA} onValueChange={setIdA}>
              <SelectTrigger className="mt-1.5 h-10 rounded-md border-border bg-surface" data-testid="compare-select-a">
                <SelectValue placeholder="Scegli…" />
              </SelectTrigger>
              <SelectContent>
                {opts.filter((o) => o.id !== idB).map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-text-secondary">Assessment B</Label>
            <Select value={idB} onValueChange={setIdB}>
              <SelectTrigger className="mt-1.5 h-10 rounded-md border-border bg-surface" data-testid="compare-select-b">
                <SelectValue placeholder="Scegli…" />
              </SelectTrigger>
              <SelectContent>
                {opts.filter((o) => o.id !== idA).map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button disabled={!idA || !idB}
                  onClick={() => navigate(`/superadmin/discovery/confronto?a=${idA}&b=${idB}`)}
                  data-testid="compare-go-btn" className="bg-accent hover:bg-accent-strong text-white">
            <ArrowLeftRight size={14} className="mr-1.5" /> Confronta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
